import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  inject,
  Injector,
  input,
  OnChanges,
  OnInit,
  Signal,
  signal,
  SimpleChanges,
  WritableSignal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ControlsOf, FormControl, FormGroup } from '@ngneat/reactive-forms';
import { TranslocoService } from '@ngneat/transloco';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { InputType } from '@vinnova/ui';
import {
  ValidateMaxDate,
  ValidateMaxOneYear,
  ValidateMaxSixMonths,
  ValidateMinDateBegaran,
} from '@vinnova/validation';
import { format, isEqual } from 'date-fns';
import {
  filter,
  Observable,
  of,
  skip,
  Subscription,
  switchMap,
  take,
  tap,
} from 'rxjs';
import {
  Begaran,
  Rapport,
  RapportTypEnum,
} from '../../../core/models/begaran.model';
import { FormQuestionDateChangeRequest } from '../../../core/models/form-group-mapping.model';
import { ReadonlyState } from '../../../core/models/form.model';
import { Question } from '../../../core/models/question/question.model';
import { StructuredDataKey } from '../../../core/models/question/structured-data-key.model';
import { RendererFormsQuery } from '../../../core/queries/renderer-forms.query';
import { RendererFormsRepository } from '../../../core/repositories/renderer-forms.repository';
import { BaseBegaranService } from '../../../core/services/base-begaran.service';
import { FormValidationService } from '../../../core/services/form-validation.service';
import { QuestionValueChangeService } from '../../../core/services/question-value-change.service';

const DATE_FORMAT = 'yyyy-MM-dd';

@UntilDestroy()
@Component({
  selector: 'lib-question-dates-change-request',
  templateUrl: './question-dates-change-request.component.html',
  styleUrls: ['./question-dates-change-request.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class QuestionDatesChangeRequestComponent implements OnInit, OnChanges {
  newReportDate: FormControl<string>;
  newProjectEndDate: FormControl<string>;
  currentReportDate: string;
  currentProjectEndDate: string;
  begaranId: string;
  reportTypeHeadline: WritableSignal<string> = signal(undefined);
  InputType = InputType;
  readOnlySignal: WritableSignal<ReadonlyState> = signal(undefined);
  readOnly$: Observable<ReadonlyState>;
  activeLang$: Observable<string>;
  activeLangSignal: WritableSignal<string> = signal(undefined);
  errorMessageReportDate: Signal<string> = signal(undefined);
  errorMessageEndDate: Signal<string> = signal(undefined);
  newReportDateReadonly: WritableSignal<string> = signal(undefined);
  newProjectEndDateReadonly: WritableSignal<string> = signal(undefined);
  newReportDateMin: Date;
  newProjectEndDateMin: Date;
  currentReportDateReadonly: WritableSignal<string> = signal(undefined);
  currentProjectEndDateReadonly: WritableSignal<string> = signal(undefined);
  valueChangeSubscription: Subscription;
  patchValueSubscription: Subscription;
  injector = inject(Injector);
  public StructuredDataKey = StructuredDataKey;
  begaran: Begaran;
  sortedNextReports: Rapport[];
  foregaendeRapport: Rapport;
  rapportTyp = RapportTypEnum;
  showChanges: Signal<boolean> = signal(false);

  question = input<Question>();
  labelPrefix = input<string>();
  repeaterGroupId = input<string>();
  questionForm = input<FormGroup<ControlsOf<FormQuestionDateChangeRequest>>>();
  value = computed(() => this.questionForm().get('value'));

  public get foregaendeRapportUnderBehandling(): boolean {
    return (
      this.foregaendeRapport &&
      (this.foregaendeRapport.bedomdDatum === null ||
        this.foregaendeRapport.bedomdDatum === undefined)
    );
  }

  constructor(
    private formValidationService: FormValidationService,
    private cdr: ChangeDetectorRef,
    private questionValueChangeService: QuestionValueChangeService,
    private translocoService: TranslocoService,
    private formsQuery: RendererFormsQuery,
    private begaranService: BaseBegaranService,
    private rendererFormRepository: RendererFormsRepository,
  ) {
    this.showChanges = toSignal(this.rendererFormRepository.showChanges$);
  }

  ngOnInit() {
    this.newReportDate = this.questionForm().get('newReportDate');
    this.newProjectEndDate = this.questionForm().get('newProjectEndDate');

    this.value()
      .touch$.pipe(take(1))
      .subscribe(() => {
        this.cdr.markForCheck();
      });
    this.readOnly$ = this.formsQuery
      .selectReadOnly()
      .pipe(tap((val) => this.readOnlySignal.set(val)));

    this.activeLang$ = this.translocoService.langChanges$;

    this.begaranService.begaran$
      .pipe(untilDestroyed(this))
      .subscribe((begaran) => {
        this.begaran = begaran;
        this.foregaendeRapport = this.begaran?.rapportLista
          ?.filter((a) => a.inskickad)
          .sort((a, b) => (a.inskickad > b.inskickad ? -1 : 1))[0];

        this.sortedNextReports = this.foregaendeRapport
          ? this.begaran?.rapportLista
              .slice()
              ?.filter((a) => !a.inskickad)
              .sort((a, b) => (a.senast < b.senast ? -1 : 1))
          : this.begaran?.rapportLista
              ?.slice()
              .sort((a, b) => (a.senast < b.senast ? -1 : 1));

        this.setErrorMessage();

        if (this.begaran) {
          this.newProjectEndDateMin = new Date(this.begaran.projektEndDate);
        }

        if (this.sortedNextReports && this.sortedNextReports?.length > 0) {
          this.reportTypeHeadline.set(
            this.translocoService.getActiveLang() === 'sv'
              ? this.sortedNextReports[0]?.titelSv
              : this.sortedNextReports[0]?.titelEn,
          );
          this.newReportDateMin = new Date(this.sortedNextReports[0]?.senast);
          this.currentReportDate = this.newReportDateMin.toDateString();
          this.newReportDate.addValidators(
            ValidateMinDateBegaran(this.newReportDateMin, 'rapport'),
          );
          const sixMonthsLater = new Date(this.sortedNextReports[0]?.senast);
          sixMonthsLater.setMonth(sixMonthsLater.getMonth() + 6);
          const nextNextReport = new Date(this.sortedNextReports[1]?.senast);
          if (
            this.sortedNextReports[0]?.typ === this.rapportTyp.Slutrapport &&
            this.sortedNextReports[0]?.senast > this.begaran?.projektEndDate
          ) {
            this.newReportDate.addValidators(
              ValidateMaxSixMonths(
                new Date(this.begaran?.projektEndDate),
                true,
              ),
            );
          } else if (
            (this.sortedNextReports[0]?.typ === this.rapportTyp.Lagesrapport ||
              this.sortedNextReports[0]?.typ === this.rapportTyp.Startrapport ||
              this.sortedNextReports[0]?.typ ===
                this.rapportTyp.Kompletteranderapport) &&
            sixMonthsLater >= nextNextReport
          ) {
            this.newReportDate.addValidators(
              ValidateMaxDate(new Date(this.sortedNextReports[1]?.senast)),
            );
          } else {
            this.newReportDate.addValidators(
              ValidateMaxSixMonths(new Date(this.sortedNextReports[0]?.senast)),
            );
          }
        }

        if (this.begaran?.projektEndDate) {
          this.currentProjectEndDate = new Date(
            this.begaran?.projektEndDate,
          ).toDateString();

          this.newProjectEndDate.addValidators([
            ValidateMaxOneYear(new Date(this.begaran?.projektEndDate)),
            ValidateMinDateBegaran(
              new Date(this.begaran?.projektEndDate),
              'projekt',
            ),
          ]);
        }
        this.newReportDate.updateValueAndValidity();
        this.newProjectEndDate.updateValueAndValidity();
      });

    this.activeLang$.pipe(untilDestroyed(this)).subscribe((lang) => {
      this.activeLangSignal.set(lang);
      if (this.sortedNextReports) {
        this.reportTypeHeadline.set(
          lang === 'sv'
            ? this.sortedNextReports[0]?.titelSv
            : this.sortedNextReports[0]?.titelEn,
        );
      }
    });

    // populate initial answers from the db
    this.questionValueChangeService.patchInitialValue(
      this.question().id,
      this.repeaterGroupId(),
      this.value(),
      this.question(),
    );

    this.readOnly$.pipe(untilDestroyed(this)).subscribe((readonly) => {
      this.readOnlySignal.set(readonly);
      if (!readonly) {
        this.formControlValueChanges(this.newReportDate, true);
        this.formControlValueChanges(this.newProjectEndDate);
      }
    });
    this.setDates();
    this.setReadonlyDates();
    this.questionValueChangeService.sendInitialKeyValue(
      this.question(),
      this.value(),
      {
        questionId: this.question().id,
        repeaterGroupId: this.repeaterGroupId(),
      },
      this.questionForm().valid,
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['repeaterGroupId']) {
      if (this.valueChangeSubscription) {
        this.valueChangeSubscription.unsubscribe();
      }
      if (this.patchValueSubscription) {
        this.patchValueSubscription.unsubscribe();
      }

      this.valueChangeSubscription = this.questionValueChangeService
        .valueChangeListener(
          this.value(),
          {
            questionId: this.question().id,
            repeaterGroupId: this.repeaterGroupId(),
          },
          this.question(),
        )
        .pipe(untilDestroyed(this))
        .subscribe();

      this.patchValueSubscription = this.questionValueChangeService
        .patchValueListener(
          this.question(),
          this.repeaterGroupId(),
          this.value(),
        )
        .pipe(untilDestroyed(this))
        .subscribe(() => {
          this.setDates();
          this.setReadonlyDates();
          this.rendererFormRepository.setShowChangesAnswers(
            'date-change-request',
            this.formHasChanges(this.newReportDate) ||
              this.formHasChanges(this.newProjectEndDate),
          );
        });
    }
  }

  private setDates() {
    const parsed = JSON.parse(this.value()?.value);
    this.newReportDate.setValue(parsed?.newReportDate);
    this.newProjectEndDate.setValue(parsed?.newProjectEndDate);
  }

  private setReadonlyDates() {
    this.newReportDateReadonly.set(
      this.newReportDate.value
        ? format(this.newReportDate.value, DATE_FORMAT)
        : undefined,
    );
    this.newProjectEndDateReadonly.set(
      this.newProjectEndDate.value
        ? format(this.newProjectEndDate.value, DATE_FORMAT)
        : undefined,
    );

    this.currentReportDateReadonly.set(
      this.currentReportDate
        ? format(this.currentReportDate, DATE_FORMAT)
        : undefined,
    );
    this.currentProjectEndDateReadonly.set(
      this.currentProjectEndDate
        ? format(this.currentProjectEndDate, DATE_FORMAT)
        : undefined,
    );
  }

  setErrorMessage(): void {
    this.errorMessageReportDate = this.checkError(this.newReportDate);
    this.errorMessageEndDate = this.checkError(this.newProjectEndDate);
  }

  formHasChanges(formControl: FormControl<string>): boolean {
    const isReportDate = formControl === this.newReportDate;
    const currentDate = isReportDate
      ? this.currentReportDate
      : this.currentProjectEndDate;
    const newDate = isReportDate
      ? this.newReportDate.value
      : this.newProjectEndDate.value;
    const currentDateFormated = format(new Date(currentDate), DATE_FORMAT);
    const newDateFormated = newDate
      ? format(new Date(newDate), DATE_FORMAT)
      : undefined;
    return !!newDateFormated && !isEqual(currentDateFormated, newDateFormated);
  }

  formControlValueChanges(
    formControl: FormControl<string>,
    isNewReportDate = false,
  ) {
    formControl.valueChanges
      .pipe(
        skip(1),
        untilDestroyed(this),
        filter(() => !this.formsQuery.getReadOnly()),
      )
      .subscribe(() => {
        this.setErrorMessage();
        if (!this.value().value) {
          this.value().patchValue(
            JSON.stringify({
              newReportDate: undefined,
              newProjectEndDate: undefined,
            }),
          );
        }

        if (formControl.value !== undefined && !formControl.errors) {
          const currentValue = JSON.parse(this.value().value);
          if (isNewReportDate) {
            currentValue.newReportDate =
              formControl.value === ''
                ? undefined
                : format(formControl.value, DATE_FORMAT);
          } else {
            currentValue.newProjectEndDate =
              formControl.value === ''
                ? undefined
                : format(formControl.value, DATE_FORMAT);
          }
          if (this.value().value !== JSON.stringify(currentValue)) {
            this.value().patchValue(JSON.stringify(currentValue));
          }
          this.value().markAsDirty();
          this.cdr.detectChanges();

          this.rendererFormRepository.setShowChangesAnswers(
            'date-change-request',
            this.formHasChanges(formControl),
          );
        }
      });
  }

  private checkError(control: FormControl<string>) {
    return toSignal(
      this.readOnly$.pipe(
        switchMap((readonly) => {
          switch (readonly) {
            case ReadonlyState.Editable: {
              return this.formValidationService
                .questionInvalid(control, this.question().id)
                .pipe(
                  switchMap((invalid) => {
                    return invalid
                      ? this.getErrorMessage(control)
                      : of(undefined);
                  }),
                );
            }
            case ReadonlyState.Locked: {
              return this.getErrorMessage(control);
            }
            case ReadonlyState.Reader: {
              return of(undefined);
            }
          }
        }),
      ),
      { injector: this.injector },
    );
  }

  getErrorMessage(control: FormControl<string>): Observable<string> {
    if (control.errors?.oneYearLater) {
      return this.translocoService.selectTranslate(
        'begaran_new_end_date_error',
      );
    } else if (control.errors?.sixMonthsLater) {
      return this.translocoService.selectTranslate(
        'begaran_new_report_date_error',
      );
    } else if (control.errors?.sixMonthsProjectEnd) {
      return this.translocoService.selectTranslate(
        'begaran_new_report_date_project_end_error',
      );
    } else if (control.errors?.maxDate) {
      return this.translocoService.selectTranslate(
        'begaran_new_report_date_max_error',
      );
    } else if (control.errors?.minDateProjekt) {
      return this.translocoService.selectTranslate(
        'datepicker_validation_endDate_inRange',
        {
          startDate: format(this.begaran.projektEndDate, DATE_FORMAT),
        },
      );
    } else if (control.errors?.minDateReport) {
      return this.translocoService.selectTranslate(
        'datepicker_validation_endDate_inRange',
        {
          startDate: format(this.newReportDateMin, DATE_FORMAT),
        },
      );
    }
    return of(undefined);
  }
}
